import { createTheme, alpha } from '@mui/material/styles';
import type { Theme, ThemeOptions } from '@mui/material/styles';
import { breakpointValues } from './breakpoints';
import { componentOverrides } from './overrides';
import { accent, darkPalette, lightPalette } from './palette';
import { customShadows, elevationShadows } from './shadows';
import { fontFamilyMono, typographyOptions } from './typography';
import {
  chartSeries,
  layout,
  tones,
  transitions,
  zIndex,
  type SemanticTone,
  type ShadowTokens,
  type ToneColors,
} from './tokens';

declare module '@mui/material/styles' {
  interface Theme {
    tones: Record<SemanticTone, ToneColors>;
    customShadows: ShadowTokens;
    layout: typeof layout;
    chartSeries: readonly string[];
    motion: typeof transitions;
    fontFamilyMono: string;
  }
  interface ThemeOptions {
    tones?: Record<SemanticTone, ToneColors>;
    customShadows?: ShadowTokens;
    layout?: typeof layout;
    chartSeries?: readonly string[];
    motion?: typeof transitions;
    fontFamilyMono?: string;
  }
}

export type ColorMode = 'light' | 'dark';

export function createAppTheme(mode: ColorMode): Theme {
  const palette = mode === 'light' ? lightPalette : darkPalette;
  const accentColor = mode === 'light' ? accent.light : accent.dark;

  const base = createTheme({
    palette: {
      ...palette,
      action: {
        hover: alpha(mode === 'light' ? '#1B2430' : '#F3F5F8', mode === 'light' ? 0.05 : 0.07),
        selected: alpha(accentColor, mode === 'light' ? 0.12 : 0.16),
        focus: alpha(accentColor, 0.28),
      },
    },
    typography: typographyOptions,
    spacing: 4,
    shape: { borderRadius: 4 },
    shadows: elevationShadows,
    transitions: {
      duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 250,
        complex: 300,
        enteringScreen: 250,
        leavingScreen: 250,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    breakpoints: { values: { ...breakpointValues } },
    tones: tones[mode],
    customShadows: {
      ...customShadows,
      focus: `0 0 0 2px ${alpha(accentColor, 0.72)}`,
    },
    layout,
    chartSeries,
    motion: transitions,
    fontFamilyMono,
    zIndex: {
      appBar: zIndex.topbar,
      drawer: zIndex.sidebar,
      modal: zIndex.modal,
      tooltip: zIndex.toast,
    },
  });

  const responsiveType: ThemeOptions['typography'] = {
    h1: {
      fontSize: '1.25rem',
      [base.breakpoints.up('md')]: { fontSize: '1.5rem' },
    },
    h2: {
      fontSize: '1.125rem',
      [base.breakpoints.up('md')]: { fontSize: '1.25rem' },
    },
  };

  return createTheme(base, {
    typography: responsiveType,
    components: componentOverrides(base),
  });
}

export { darkPalette, lightPalette } from './palette';
export { fontFamily, fontFamilyMono } from './typography';
